<template>
    <div class="tw:w-full" :class="props.customClass">
        <ValidationProvider
            :name="props.label || props.name"
            :rules="computedRules"
            :value="localValue"
            v-slot="{ errors }"
            mode="eager"
            ref="provider"
        >
            <div
                ref="root"
                class="tw:relative tw:w-full"
                :data-validation-error="errors[0] ? '' : undefined"
                @keydown.esc.stop="handleEscape"
            >
                <!-- vee-validate v3 detecta el valor del campo en el primer nodo
                     con `value` del slot. El control visible es un <button>, así
                     que sin este input oculto el provider ve `undefined` y
                     `required` falla aunque haya fecha (modales precargados). -->
                <input type="hidden" :value="localValue" />
                <button
                    type="button"
                    :id="props.id"
                    :data-cy="props.dataCy || props.name"
                    :data-value="localValue"
                    :data-min="minValue || undefined"
                    :data-max="maxValue || undefined"
                    data-datetime-picker="true"
                    class="tw:relative tw:flex tw:w-full tw:items-center tw:justify-between tw:gap-2 tw:rounded tw:border tw:p-2 tw:text-left tw:text-sm tw:focus:outline-none tw:disabled:cursor-not-allowed tw:disabled:bg-slate-50 tw:disabled:text-slate-400"
                    :class="errors[0]
                        ? 'tw:border-red-500 tw:bg-red-50/50 tw:focus:border-blue-400'
                        : triggerClasses"
                    :disabled="isDisabled"
                    :aria-expanded="open ? 'true' : 'false'"
                    aria-haspopup="dialog"
                    @click="togglePanel"
                >
                    <!-- Label flotante, como BaseInputText/BaseMultiselect: hace
                         de placeholder cuando no hay valor y sube al borde al
                         abrir el panel o al elegir una fecha. -->
                    <span
                        v-if="props.label"
                        data-cy="datetime-label"
                        class="tw:absolute tw:pointer-events-none tw:uppercase tw:tracking-wide tw:duration-200 tw:origin-left tw:start-1 tw:bg-white tw:px-1"
                        :class="[
                            labelFloated
                                ? 'tw:-top-[10px] tw:scale-75 tw:font-bold'
                                : 'tw:top-1/2 tw:-translate-y-1/2 tw:text-sm tw:font-normal',
                            errors[0] ? 'tw:text-red-400' : (labelFloated ? 'tw:text-slate-600' : 'tw:text-slate-400')
                        ]"
                    >
                        {{ props.label }}
                    </span>
                    <span
                        class="tw:truncate"
                        :class="localValue ? 'tw:text-slate-900' : 'tw:text-slate-400'"
                    >
                        {{ valueText }}
                    </span>
                    <i class="fas fa-calendar-alt tw:shrink-0 tw:text-slate-400" aria-hidden="true"></i>
                </button>

                <div
                    v-if="open"
                    :id="panelId"
                    data-cy="datetime-panel"
                    role="dialog"
                    :aria-label="props.label || props.name || 'Seleccionar fecha y hora'"
                    class="tw:absolute tw:left-0 tw:z-50 tw:mt-2 tw:w-72 tw:rounded-xl tw:border tw:border-slate-200 tw:bg-white tw:p-3 tw:shadow-xl"
                >
                    <div class="tw:flex tw:items-center tw:justify-between tw:gap-1">
                        <Button
                            variant="pagination"
                            icon="fas fa-chevron-left"
                            custom-class="tw:border-0 tw:transition-colors tw:hover:bg-slate-100 tw:hover:text-slate-700"
                            data-cy="datetime-prev"
                            aria-label="Anterior"
                            title="Anterior"
                            @click="shiftView(-1)"
                        />
                        <button
                            v-if="view !== 'years'"
                            type="button"
                            data-cy="datetime-view-toggle"
                            class="tw:flex tw:items-center tw:gap-1.5 tw:rounded-md tw:px-2 tw:py-1 tw:text-sm tw:font-semibold tw:leading-none tw:text-slate-900 tw:capitalize tw:hover:bg-slate-100"
                            :data-month="monthKey"
                            :aria-label="view === 'days' ? 'Elegir mes y año' : 'Elegir año'"
                            :title="view === 'days' ? 'Elegir mes y año' : 'Elegir año'"
                            @click="openNextView"
                        >
                            {{ headerLabel }}
                            <i class="fas fa-chevron-down tw:relative tw:top-px tw:text-[10px] tw:leading-none tw:text-slate-400" aria-hidden="true"></i>
                        </button>
                        <span
                            v-else
                            class="tw:px-2 tw:py-1 tw:text-sm tw:font-semibold tw:text-slate-900"
                            :data-month="monthKey"
                        >
                            {{ headerLabel }}
                        </span>
                        <Button
                            variant="pagination"
                            icon="fas fa-chevron-right"
                            custom-class="tw:border-0 tw:transition-colors tw:hover:bg-slate-100 tw:hover:text-slate-700"
                            data-cy="datetime-next"
                            aria-label="Siguiente"
                            title="Siguiente"
                            @click="shiftView(1)"
                        />
                    </div>

                    <!-- Nivel 1: días -->
                    <template v-if="view === 'days'">
                        <div class="tw:mt-3 tw:grid tw:grid-cols-7 tw:gap-1 tw:text-center tw:text-[11px] tw:font-semibold tw:uppercase tw:text-slate-400">
                            <span v-for="dayName in WEEKDAYS" :key="dayName">{{ dayName }}</span>
                        </div>

                        <div class="tw:mt-1 tw:grid tw:grid-cols-7 tw:gap-1">
                            <button
                                v-for="day in days"
                                :key="day.value"
                                type="button"
                                :data-day="day.value"
                                :disabled="day.disabled"
                                :aria-pressed="day.selected ? 'true' : 'false'"
                                class="tw:flex tw:h-8 tw:w-full tw:items-center tw:justify-center tw:rounded-md tw:text-sm tw:transition-colors"
                                :class="dayClasses(day)"
                                @click="selectDay(day)"
                            >
                                {{ day.label }}
                            </button>
                        </div>

                        <div class="tw:mt-3 tw:flex tw:items-center tw:justify-between tw:gap-2 tw:border-t tw:border-slate-200 tw:pt-3">
                            <span class="tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-slate-500">Hora</span>
                            <div class="tw:flex tw:items-center tw:gap-1.5">
                                <BaseSelect
                                    compact
                                    data-cy="datetime-hours"
                                    aria-label="Hora"
                                    :value="draftHours"
                                    :options="hourOptions"
                                    @change="onHoursChange"
                                />
                                <span class="tw:text-slate-400">:</span>
                                <BaseSelect
                                    compact
                                    data-cy="datetime-minutes"
                                    aria-label="Minutos"
                                    :value="draftMinutes"
                                    :options="minuteOptions"
                                    @change="onMinutesChange"
                                />
                            </div>
                        </div>

                        <div class="tw:mt-3 tw:flex tw:items-center tw:justify-between">
                            <button
                                type="button"
                                data-cy="datetime-today"
                                class="tw:text-xs tw:font-semibold tw:text-sky-600 tw:hover:text-sky-700 tw:disabled:cursor-not-allowed tw:disabled:text-slate-300"
                                :disabled="!canSelectToday"
                                @click="selectToday"
                            >
                                Hoy
                            </button>
                            <button
                                type="button"
                                data-cy="datetime-clear"
                                class="tw:text-xs tw:font-semibold tw:text-slate-500 tw:hover:text-slate-700"
                                @click="clearValue"
                            >
                                Limpiar
                            </button>
                        </div>
                    </template>

                    <!-- Nivel 2: meses del año en curso -->
                    <div
                        v-else-if="view === 'months'"
                        class="tw:mt-3 tw:grid tw:grid-cols-3 tw:gap-1"
                        data-cy="datetime-months"
                    >
                        <button
                            v-for="month in monthOptions"
                            :key="month.key"
                            type="button"
                            data-cy="datetime-month-option"
                            :data-month-option="month.key"
                            :disabled="month.disabled"
                            :aria-pressed="month.selected ? 'true' : 'false'"
                            class="tw:flex tw:h-9 tw:items-center tw:justify-center tw:rounded-md tw:text-sm tw:transition-colors"
                            :class="optionClasses(month)"
                            @click="selectMonth(month)"
                        >
                            {{ month.label }}
                        </button>
                    </div>

                    <!-- Nivel 3: años de la década en curso -->
                    <div
                        v-else
                        class="tw:mt-3 tw:grid tw:grid-cols-4 tw:gap-1"
                        data-cy="datetime-years"
                    >
                        <button
                            v-for="year in yearOptions"
                            :key="year.value"
                            type="button"
                            data-cy="datetime-year-option"
                            :data-year-option="year.value"
                            :disabled="year.disabled"
                            :aria-pressed="year.selected ? 'true' : 'false'"
                            class="tw:flex tw:h-9 tw:items-center tw:justify-center tw:rounded-md tw:text-sm tw:transition-colors"
                            :class="optionClasses(year)"
                            @click="selectYear(year)"
                        >
                            {{ year.value }}
                        </button>
                    </div>
                </div>
            </div>

            <p
                v-if="disabledHintText"
                data-cy="datetime-disabled-hint"
                class="tw:mt-1 tw:text-xs tw:text-slate-500"
            >
                {{ disabledHintText }}
            </p>

            <span
                v-if="errors[0]"
                class="tw:block tw:mt-1 tw:text-xs tw:text-red-600 tw:font-medium"
            >
                {{ formatErrorMessage(errors[0]) }}
            </span>
            <span
                v-else-if="props.apiValidationError"
                class="tw:block tw:mt-1 tw:text-xs tw:text-red-600 tw:font-medium"
            >
                {{ Array.isArray(props.apiValidationError) ? props.apiValidationError.join(', ') : props.apiValidationError }}
            </span>
        </ValidationProvider>
    </div>
</template>

<script setup>
    import { ref, computed, watch, onUnmounted } from 'vue';
    import { toDateTimeInputValue, formatDateTimeLabel } from '@/utils/dateUtils';
    import BaseSelect from '@/modules/admin/ui/BaseSelect.vue';
    import Button from '@/modules/admin/ui/Button.vue';

    /**
     * BaseDateTimePicker — date + time picker de admin con estética shadcn
     * (trigger + popover + calendario), sobre Vue 2.7.
     *
     * Sustituye a BaseDateTimeInput en los formularios que necesitan un
     * calendario controlado por la app (el nativo no permite posicionar el mes
     * inicial ni explicar por qué un día está deshabilitado).
     *
     * Contrato público idéntico al de BaseDateTimeInput:
     *   - v-model (`modelValue`) o el legacy `value` + `@input`;
     *   - los valores viajan siempre como "YYYY-MM-DDTHH:mm" (hora de pared);
     *   - `min` / `max` acotan los días seleccionables (y la hora se recorta al
     *     rango cuando cae fuera).
     */

    const WEEKDAYS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
    const MONTHS = [
        'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
        'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
    ];
    const MONTHS_SHORT = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
    const HOURS = Array.from({ length: 24 }, (unused, index) => index);
    const MINUTES = Array.from({ length: 60 }, (unused, index) => index);

    const props = defineProps({
        // v-model contract (Vue 2.7 Composition API)
        modelValue: { type: String, default: null },
        // legacy contract (:value + @input)
        value: { type: String, default: null },
        label: String,
        name: String,
        id: String,
        dataCy: String,
        rules: [String, Object],
        min: { type: String, default: null },
        max: { type: String, default: null },
        disabled: Boolean,
        apiValidationError: [String, Array],
        customClass: String,
        placeholder: { type: String, default: 'Seleccionar fecha y hora' },
        // Motivo visible cuando el formulario deshabilita el control.
        disabledHint: { type: String, default: '' }
    });

    const emit = defineEmits(['update:modelValue', 'input']);

    const computedRules = computed(() => props.rules || '');

    const pad = value => String(value).padStart(2, '0');

    // Opciones de los `BaseSelect` de hora y minuto. El valor del picker sigue
    // siendo "HH:mm"; los selects sólo lo pintan.
    const hourOptions = HOURS.map(hour => ({ value: hour, label: pad(hour) }));
    const minuteOptions = MINUTES.map(minute => ({ value: minute, label: pad(minute) }));

    const todayValue = () => {
        const now = new Date();
        return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    };

    const localValue = ref(toDateTimeInputValue(props.modelValue ?? props.value));
    const open = ref(false);
    const root = ref(null);
    // Nivel visible del popover: días → meses → años.
    const view = ref('days');

    const viewYear = ref(new Date().getFullYear());
    const viewMonth = ref(new Date().getMonth());
    const draftHours = ref(0);
    const draftMinutes = ref(0);

    const minValue = computed(() => toDateTimeInputValue(props.min) || null);
    const maxValue = computed(() => toDateTimeInputValue(props.max) || null);

    // Un rango invertido dejaría TODOS los días deshabilitados (el picker
    // parecería roto). Ante datos inconsistentes se ignoran los límites para
    // pintar el calendario, pero el campo se DESHABILITA: no hay ninguna fecha
    // que cumpla min y max a la vez, así que no se puede ofrecer una selección
    // válida. El consumidor muestra el aviso (EventoSales lo hace).
    const hasValidRange = computed(() =>
        !minValue.value || !maxValue.value || maxValue.value >= minValue.value
    );

    const isDisabled = computed(() => props.disabled || !hasValidRange.value);

    /**
     * Motivo visible bajo el campo cuando está deshabilitado. El componente no
     * inventa mensajes: cada formulario explica su caso con `disabledHint` para
     * que el usuario lea algo comprensible y no un término técnico.
     */
    const disabledHintText = computed(() => {
        if (!isDisabled.value) return '';
        return props.disabledHint || '';
    });

    const minDay = computed(() =>
        hasValidRange.value && minValue.value ? minValue.value.slice(0, 10) : null
    );
    const maxDay = computed(() =>
        hasValidRange.value && maxValue.value ? maxValue.value.slice(0, 10) : null
    );

    const valueDate = computed(() => localValue.value ? localValue.value.slice(0, 10) : '');
    const valueTime = computed(() => localValue.value ? localValue.value.slice(11, 16) : '');
    const displayValue = computed(() => formatDateTimeLabel(localValue.value));

    // El label hace de placeholder hasta que hay valor o se abre el panel.
    const labelFloated = computed(() => !!(localValue.value || open.value));
    const valueText = computed(() => {
        if (localValue.value) return displayValue.value;
        return props.label ? '' : props.placeholder;
    });

    const panelId = computed(() => (props.id ? `${props.id}-panel` : 'datetime-panel'));

    const monthKey = computed(() => `${viewYear.value}-${pad(viewMonth.value + 1)}`);

    const selectedYear = computed(() =>
        valueDate.value ? Number(valueDate.value.slice(0, 4)) : null
    );
    const selectedMonth = computed(() =>
        valueDate.value ? Number(valueDate.value.slice(5, 7)) - 1 : null
    );

    // Década visible en el nivel de años (2020 → 2020–2029). Las flechas
    // mueven ±10 años, así que desde 2026 se llega a 1998 en tres clics en vez
    // de 336 clics mes a mes.
    const decadeStart = computed(() => Math.floor(viewYear.value / 10) * 10);

    const headerLabel = computed(() => {
        if (view.value === 'years') return `${decadeStart.value} – ${decadeStart.value + 9}`;
        if (view.value === 'months') return String(viewYear.value);
        return `${MONTHS[viewMonth.value]} ${viewYear.value}`;
    });

    const daysInMonth = (year, monthIndex) => new Date(year, monthIndex + 1, 0).getDate();

    const monthOptions = computed(() => MONTHS_SHORT.map((label, index) => {
        const key = `${viewYear.value}-${pad(index + 1)}`;
        const first = `${key}-01`;
        const last = `${key}-${pad(daysInMonth(viewYear.value, index))}`;

        return {
            key,
            label,
            monthIndex: index,
            selected: viewYear.value === selectedYear.value && index === selectedMonth.value,
            disabled: (minDay.value && last < minDay.value) || (maxDay.value && first > maxDay.value)
        };
    }));

    const yearOptions = computed(() => Array.from({ length: 10 }, (unused, index) => {
        const year = decadeStart.value + index;

        return {
            value: year,
            selected: year === selectedYear.value,
            disabled: (minDay.value && `${year}-12-31` < minDay.value) || (maxDay.value && `${year}-01-01` > maxDay.value)
        };
    }));

    const triggerClasses = computed(() => {
        if (isDisabled.value) return 'tw:border-slate-300 tw:bg-white';
        return 'tw:border-slate-300 tw:bg-white tw:hover:border-slate-400 tw:focus:border-blue-400';
    });

    const canSelectToday = computed(() => {
        const today = todayValue();
        return (!minDay.value || today >= minDay.value) && (!maxDay.value || today <= maxDay.value);
    });

    const days = computed(() => {
        const first = new Date(viewYear.value, viewMonth.value, 1);
        const offset = (first.getDay() + 6) % 7;
        const today = todayValue();

        return Array.from({ length: 42 }, (unused, index) => {
            const date = new Date(viewYear.value, viewMonth.value, 1 - offset + index);
            const value = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

            return {
                key: value,
                value,
                label: date.getDate(),
                outside: date.getMonth() !== viewMonth.value,
                disabled: (minDay.value && value < minDay.value) || (maxDay.value && value > maxDay.value),
                selected: value === valueDate.value,
                today: value === today
            };
        });
    });

    const dayClasses = day => {
        const classes = [];

        if (day.selected) classes.push('tw:bg-slate-900 tw:text-white tw:hover:bg-slate-800');
        else if (day.disabled) classes.push('tw:text-slate-300 tw:cursor-not-allowed');
        else if (day.outside) classes.push('tw:text-slate-400 tw:hover:bg-slate-100');
        else classes.push('tw:text-slate-700 tw:hover:bg-slate-100');

        if (day.today) classes.push('tw:font-semibold tw:ring-1 tw:ring-slate-300');

        return classes.join(' ');
    };

    const optionClasses = option => {
        if (option.selected) return 'tw:bg-slate-900 tw:text-white tw:hover:bg-slate-800';
        if (option.disabled) return 'tw:text-slate-300 tw:cursor-not-allowed';
        return 'tw:text-slate-700 tw:hover:bg-slate-100';
    };

    const formatErrorMessage = err => {
        const msg = typeof err === 'string' ? err : (err && err.message) || String(err || '');
        return msg ? msg.charAt(0).toUpperCase() + msg.slice(1).toLowerCase() : '';
    };

    const clampValue = value => {
        if (!value) return '';
        // Con un rango invertido (min > max) no hay límite válido: el
        // calendario ya ignora `minDay`/`maxDay`, así que el valor elegido no
        // debe sustituirse por uno de los extremos. Sustituirlo en silencio
        // dejaba seleccionada una fecha que el usuario no había elegido; el
        // conflicto se resuelve con la validación visible del formulario.
        if (!hasValidRange.value) return value;
        if (minValue.value && value < minValue.value) return minValue.value;
        if (maxValue.value && value > maxValue.value) return maxValue.value;
        return value;
    };

    function setValue(value) {
        if (isDisabled.value) return;

        const next = clampValue(value);
        if (next === localValue.value) return;
        localValue.value = next;
        // Emit both contracts: v-model listeners use update:modelValue,
        // legacy callers listen to input.
        emit('update:modelValue', next);
        emit('input', next);
    }

    function openPanel() {
        if (isDisabled.value) return;

        // El mes visible debe caer dentro del rango: si el evento es de 2034 y
        // se abriera en el mes actual, todos los días aparecerían deshabilitados.
        const today = todayValue();
        let base = valueDate.value;

        if (!base) {
            base = minDay.value && minDay.value > today ? minDay.value : today;
            if (maxDay.value && base > maxDay.value) base = maxDay.value;
        }

        viewYear.value = Number(base.slice(0, 4));
        viewMonth.value = Number(base.slice(5, 7)) - 1;

        const time = valueTime.value;
        draftHours.value = time ? Number(time.slice(0, 2)) : 0;
        draftMinutes.value = time ? Number(time.slice(3, 5)) : 0;

        view.value = 'days';
        open.value = true;
    }

    function closePanel() {
        open.value = false;
    }

    function handleEscape() {
        if (view.value === 'years') {
            view.value = 'months';
            return;
        }

        if (view.value === 'months') {
            view.value = 'days';
            return;
        }

        closePanel();
    }

    function togglePanel() {
        if (open.value) closePanel();
        else openPanel();
    }

    function shiftMonth(delta) {
        const date = new Date(viewYear.value, viewMonth.value + delta, 1);
        viewYear.value = date.getFullYear();
        viewMonth.value = date.getMonth();
    }

    // Las flechas cambian de escala según el nivel: ±1 mes, ±1 año o ±1 década.
    function shiftView(delta) {
        if (view.value === 'days') {
            shiftMonth(delta);
            return;
        }

        viewYear.value += view.value === 'months' ? delta : delta * 10;
    }

    function openNextView() {
        if (view.value === 'days') view.value = 'months';
        else if (view.value === 'months') view.value = 'years';
    }

    function selectMonth(month) {
        if (month.disabled) return;

        viewMonth.value = month.monthIndex;
        view.value = 'days';
    }

    function selectYear(year) {
        if (year.disabled) return;

        viewYear.value = year.value;
        view.value = 'months';
    }

    function commitTime() {
        let date = valueDate.value;

        if (!date) {
            const today = todayValue();
            if (minDay.value && today < minDay.value) date = minDay.value;
            else if (maxDay.value && today > maxDay.value) date = maxDay.value;
            else date = today;
        }

        setValue(`${date}T${pad(draftHours.value)}:${pad(draftMinutes.value)}`);
    }

    function selectDay(day) {
        if (day.disabled) return;

        if (day.outside) {
            const date = new Date(`${day.value}T00:00`);
            viewYear.value = date.getFullYear();
            viewMonth.value = date.getMonth();
        }

        setValue(`${day.value}T${pad(draftHours.value)}:${pad(draftMinutes.value)}`);
    }

    function onHoursChange(value) {
        draftHours.value = Number(value);
        commitTime();
    }

    function onMinutesChange(value) {
        draftMinutes.value = Number(value);
        commitTime();
    }

    function selectToday() {
        if (!canSelectToday.value) return;
        setValue(`${todayValue()}T${pad(draftHours.value)}:${pad(draftMinutes.value)}`);
    }

    function clearValue() {
        if (isDisabled.value) return;

        localValue.value = '';
        emit('update:modelValue', '');
        emit('input', '');
        closePanel();
    }

    // Sync localValue when the parent pushes a new value via either contract
    // (e.g. submit reset, reactive update from a watcher in the parent).
    watch([() => props.modelValue, () => props.value], ([modelValue, value]) => {
        const incoming = toDateTimeInputValue(modelValue ?? value);
        if (incoming !== localValue.value) {
            localValue.value = incoming;
        }
    });

    // Keep the time selects in sync with the committed value (which may have
    // been clamped to min/max).
    watch(localValue, value => {
        if (!value) return;
        draftHours.value = Number(value.slice(11, 13));
        draftMinutes.value = Number(value.slice(14, 16));
    });

    watch(isDisabled, disabled => {
        if (disabled) closePanel();
    });

    const onDocumentPointerDown = event => {
        if (root.value && !root.value.contains(event.target)) closePanel();
    };

    watch(open, isOpen => {
        if (typeof document === 'undefined') return;
        if (isOpen) document.addEventListener('mousedown', onDocumentPointerDown, true);
        else document.removeEventListener('mousedown', onDocumentPointerDown, true);
    });

    onUnmounted(() => {
        if (typeof document === 'undefined') return;
        document.removeEventListener('mousedown', onDocumentPointerDown, true);
    });
</script>

<script>
    export default {
        name: 'BaseDateTimePicker',
        inheritAttrs: false
    };
</script>
